// Card-4.10 (2026-04-29) — Vuex module para administração de
// PipelineSequenceDefinitions. State machine simples: list+selected+loading.
// Componentes admin (Index/Form) consomem via getters/dispatch.

import PipelineSequenceDefinitionsAPI from 'dashboard/api/pipelineSequenceDefinitions';

const state = {
  records: [],
  selected: null,
  loading: false,
  saving: false,
  error: null,
  meta: { current_page: 1, total_count: 0, total_pages: 1 },
};

const getters = {
  getSequenceDefinitions: _state => _state.records,
  getSequenceDefinitionById: _state => id =>
    _state.records.find(d => d.id === Number(id)),
  getSelectedSequenceDefinition: _state => _state.selected,
  getSequenceDefinitionsMeta: _state => _state.meta,
  isFetchingSequenceDefinitions: _state => _state.loading,
  isSavingSequenceDefinition: _state => _state.saving,
  getSequenceDefinitionsError: _state => _state.error,
};

const actions = {
  async fetch({ commit }, params = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.list(params);
      commit('SET_RECORDS', data.data || []);
      commit('SET_META', data.meta || {});
    } catch (e) {
      commit('SET_ERROR', e.response?.data?.error || e.message);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async fetchOne({ commit }, id) {
    commit('SET_LOADING', true);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.show(id);
      commit('SET_SELECTED', data.data);
      return data.data;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async create({ commit }, payload) {
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.create(payload);
      commit('UPSERT_RECORD', data.data);
      return data.data;
    } catch (e) {
      const msg =
        e.response?.data?.details?.join(', ') ||
        e.response?.data?.error ||
        e.message;
      commit('SET_ERROR', msg);
      throw e;
    } finally {
      commit('SET_SAVING', false);
    }
  },

  async update({ commit }, { id, payload }) {
    commit('SET_SAVING', true);
    commit('SET_ERROR', null);
    try {
      const { data } = await PipelineSequenceDefinitionsAPI.update(id, payload);
      commit('UPSERT_RECORD', data.data);
      commit('SET_SELECTED', data.data);
      return data.data;
    } catch (e) {
      const msg =
        e.response?.data?.details?.join(', ') ||
        e.response?.data?.error ||
        e.message;
      commit('SET_ERROR', msg);
      throw e;
    } finally {
      commit('SET_SAVING', false);
    }
  },

  async delete({ commit }, id) {
    await PipelineSequenceDefinitionsAPI.delete(id);
    commit('REMOVE_RECORD', id);
  },

  async toggleActive({ commit }, { id, active }) {
    const action = active ? 'activate' : 'deactivate';
    const { data } = await PipelineSequenceDefinitionsAPI[action](id);
    commit('UPSERT_RECORD', data.data);
  },

  async duplicate({ commit }, { id, newName }) {
    const { data } = await PipelineSequenceDefinitionsAPI.duplicate(
      id,
      newName
    );
    commit('UPSERT_RECORD', data.data);
    return data.data;
  },
};

const mutations = {
  SET_LOADING(_state, v) {
    _state.loading = v;
  },
  SET_SAVING(_state, v) {
    _state.saving = v;
  },
  SET_ERROR(_state, v) {
    _state.error = v;
  },
  SET_RECORDS(_state, records) {
    _state.records = records;
  },
  SET_META(_state, meta) {
    _state.meta = { ..._state.meta, ...meta };
  },
  SET_SELECTED(_state, record) {
    _state.selected = record;
  },
  UPSERT_RECORD(_state, record) {
    const idx = _state.records.findIndex(r => r.id === record.id);
    if (idx >= 0) _state.records.splice(idx, 1, record);
    else _state.records.unshift(record);
  },
  REMOVE_RECORD(_state, id) {
    _state.records = _state.records.filter(r => r.id !== id);
    if (_state.selected?.id === id) _state.selected = null;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
